import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';
import LocationSelect from '../components/LocationSelect';
import { joinHeadquarters } from '../lib/locations';

const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger:  '#C0222A',
};

const STRATEGIES: Record<string, string[]> = {
  hedge_fund: ['Long/Short Equity','Global Macro','Quant','Multi-Strategy','Event Driven','Merger Arbitrage','Fixed Income Arbitrage','CTA / Managed Futures','Market Neutral','Distressed'],
  pe:         ['Buyout','Growth Equity','Turnaround','Co-investment','Secondary','Infrastructure PE'],
  vc:         ['Pre-Seed','Seed','Series A','Series B+','Deep Tech','Fintech','HealthTech','SaaS','Consumer','Climate Tech'],
  re:         ['Core','Core Plus','Value Add','Opportunistic','Debt','Development','REIT'],
  credit:     ['Direct Lending','Mezzanine','Distressed Debt','CLO','Trade Finance','Structured Credit'],
  infrastructure: ['Core Infrastructure','Renewables','Digital Infrastructure','Transport','Social Infrastructure'],
};

const VEHICLE_TYPES = ['Cayman LP','Cayman LLC','Delaware LP','Delaware LLC','UCITS','AIFMD AIF','Luxembourg SICAV','Irish ICAV','Guernsey LP','Jersey LP','UK LLP','Open-ended Fund','Closed-ended Fund','Co-investment Vehicle','SMA','SPAC'];

const REGIONAL_FOCUS = ['Global','North America','Europe','Asia Pacific','Latin America','Middle East & Africa','UK','US','China','India','Southeast Asia','Emerging Markets','Developed Markets','Pan-European','Nordic'];

const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white,
  border: `1px solid ${C.border}`, borderRadius: '3px',
  color: C.text, fontSize: '13px',
  padding: '8px 10px', outline: 'none',
  fontFamily: 'system-ui', marginBottom: '14px',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '10px',
  fontWeight: 700, color: C.muted,
  marginBottom: '5px', letterSpacing: '0.6px',
  textTransform: 'uppercase', fontFamily: 'system-ui',
};

const sectionCard: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', padding: '20px 22px', marginBottom: '14px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, color: C.navy,
  letterSpacing: '0.8px', textTransform: 'uppercase',
  marginBottom: '18px', paddingBottom: '10px',
  borderBottom: `1px solid ${C.borderLt}`,
};

export default function CreateFund() {
  const navigate = useNavigate();
  const { firm }  = useProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [form, setForm]       = useState({
    name: '', asset_class: '', strategy: '', description: '',
    aum_min: '', aum_max: '', inception_date: '',
    country: '', city: '', regional_focus: '', vehicle_type: '',
    management_fee: '', performance_fee: '', minimum_subscription: '',
    redemption_period: '', lockup_months: '',
    open_to_sma: false, status: 'open',
  });

  const update = (field: string, value: any) => setForm(prev => ({ ...prev, [field]: value }));

  const availableStrategies = form.asset_class ? STRATEGIES[form.asset_class] || [] : [];
  const headquarters        = joinHeadquarters(form.country, form.city);

  const focusStyle = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    (e.target.style.borderColor = C.navy);
  const blurStyle  = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    (e.target.style.borderColor = C.border);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firm) { setError('No firm found. Please complete your profile first.'); return; }
    setLoading(true); setError('');
    const { error } = await supabase.from('funds').insert({
      firm_id: firm.id, name: form.name, asset_class: form.asset_class,
      strategy: form.strategy, description: form.description,
      aum_min: form.aum_min ? parseFloat(form.aum_min) : null,
      aum_max: form.aum_max ? parseFloat(form.aum_max) : null,
      inception_date: form.inception_date || null, headquarters,
      regional_focus: form.regional_focus, vehicle_type: form.vehicle_type,
      management_fee: form.management_fee ? parseFloat(form.management_fee) : null,
      performance_fee: form.performance_fee ? parseFloat(form.performance_fee) : null,
      minimum_subscription: form.minimum_subscription ? parseFloat(form.minimum_subscription) : null,
      redemption_period: form.redemption_period,
      lockup_months: form.lockup_months ? parseInt(form.lockup_months) : null,
      open_to_sma: form.open_to_sma, status: form.status,
    });
    if (error) { setError(error.message); setLoading(false); }
    else navigate('/lp-connect');
  };

  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div><label style={labelStyle}>{label}</label>{children}</div>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: '720px' }}>

      <button onClick={() => navigate(-1)} style={{
        background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
        color: C.textSec, padding: '5px 12px', cursor: 'pointer',
        fontSize: '12px', marginBottom: '18px', fontFamily: 'system-ui', fontWeight: 500,
      }}>← Back</button>

      <div style={{ marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          List a Fund
        </h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
          Add your fund to AltConnect so allocators can discover and connect with you
        </p>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '10px 14px', marginBottom: '16px', fontSize: '12.5px', color: C.danger }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* Fund Overview */}
        <div style={sectionCard}>
          <div style={sectionTitle}>Fund Overview</div>

          <Field label="Fund name">
            <input style={inputStyle} type="text" value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="e.g. Principia Fund I" required
              onFocus={focusStyle} onBlur={blurStyle} />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Asset class">
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={form.asset_class}
                onChange={e => { update('asset_class', e.target.value); update('strategy', ''); }}
                required onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">Select...</option>
                <option value="hedge_fund">Hedge Fund</option>
                <option value="pe">Private Equity</option>
                <option value="vc">Venture Capital</option>
                <option value="re">Real Estate</option>
                <option value="credit">Credit</option>
                <option value="infrastructure">Infrastructure</option>
              </select>
            </Field>
            <Field label="Strategy">
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={form.strategy}
                onChange={e => update('strategy', e.target.value)}
                required disabled={!form.asset_class}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">{form.asset_class ? 'Select...' : 'Select asset class first'}</option>
                {availableStrategies.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Fund description">
            <textarea value={form.description}
              onChange={e => update('description', e.target.value)}
              placeholder="Describe your fund strategy, approach and key differentiators..."
              style={{ ...inputStyle, height: '90px', resize: 'vertical' }}
              onFocus={focusStyle} onBlur={blurStyle} />
          </Field>

          <LocationSelect
            country={form.country} city={form.city}
            onCountryChange={v => update('country', v)}
            onCityChange={v => update('city', v)}
            countryLabel="Country" cityLabel="City"
            labelStyle={labelStyle} inputStyle={inputStyle}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Regional focus">
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={form.regional_focus} onChange={e => update('regional_focus', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">Select...</option>
                {REGIONAL_FOCUS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Vehicle type">
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={form.vehicle_type} onChange={e => update('vehicle_type', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">Select...</option>
                {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Inception date">
              <input style={inputStyle} type="date" value={form.inception_date}
                onChange={e => update('inception_date', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
            <Field label="Fund status">
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={form.status} onChange={e => update('status', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="soft_closed">Soft Closed</option>
              </select>
            </Field>
          </div>
        </div>

        {/* AUM & Terms */}
        <div style={sectionCard}>
          <div style={sectionTitle}>AUM & Terms</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="AUM min ($mm)">
              <input style={inputStyle} type="number" value={form.aum_min}
                onChange={e => update('aum_min', e.target.value)} placeholder="100"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
            <Field label="AUM max ($mm)">
              <input style={inputStyle} type="number" value={form.aum_max}
                onChange={e => update('aum_max', e.target.value)} placeholder="250"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Management fee (%)">
              <input style={inputStyle} type="number" step="0.01" value={form.management_fee}
                onChange={e => update('management_fee', e.target.value)} placeholder="1.75"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
            <Field label="Performance fee (%)">
              <input style={inputStyle} type="number" step="0.01" value={form.performance_fee}
                onChange={e => update('performance_fee', e.target.value)} placeholder="20"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Minimum subscription ($mm)">
              <input style={inputStyle} type="number" step="0.1" value={form.minimum_subscription}
                onChange={e => update('minimum_subscription', e.target.value)} placeholder="5"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
            <Field label="Lockup (months)">
              <input style={inputStyle} type="number" value={form.lockup_months}
                onChange={e => update('lockup_months', e.target.value)} placeholder="0"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>
          </div>

          <Field label="Redemption period">
            <select style={{ ...inputStyle, cursor: 'pointer' }}
              value={form.redemption_period} onChange={e => update('redemption_period', e.target.value)}
              onFocus={focusStyle} onBlur={blurStyle}>
              <option value="">Select...</option>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Semi-annual">Semi-annual</option>
              <option value="Annual">Annual</option>
              <option value="Monthly with 30 days notice">Monthly with 30 days notice</option>
              <option value="Quarterly with 45 days notice">Quarterly with 45 days notice</option>
              <option value="Quarterly with 90 days notice">Quarterly with 90 days notice</option>
              <option value="At fund discretion">At fund discretion</option>
            </select>
          </Field>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <input type="checkbox" id="sma" checked={form.open_to_sma}
              onChange={e => update('open_to_sma', e.target.checked)}
              style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: C.navy }} />
            <label htmlFor="sma" style={{ fontSize: '13px', color: C.textSec, cursor: 'pointer', fontWeight: 500 }}>
              Open to SMAs (Separately Managed Accounts)
            </label>
          </div>
        </div>

        <button type="submit" disabled={loading} style={{
          width: '100%', background: C.navy, border: 'none',
          borderRadius: '3px', color: C.white,
          fontSize: '13px', fontWeight: 600, padding: '11px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: 'system-ui', opacity: loading ? 0.7 : 1,
          letterSpacing: '0.2px',
        }}>
          {loading ? 'Listing fund...' : 'List Fund on AltConnect'}
        </button>
      </form>
    </div>
  );
}